import { useEffect } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useStores } from '../../stores/useStores';

import { postUiReady } from './postUiReady';
import { mapShellDeepLink, onShellDeepLink, toDeepLinkUrl } from './shellBridge';

/**
 * Routes the shell's deep links and notification taps in-app. Called from `Layout`, the router's root, so
 * it is mounted on every route and reads the stores through context — `App` itself must not import them at
 * module scope (it would pull `RootStore` and everything behind it into whatever imports `App`).
 * A no-op outside the shell, where nothing is ever delivered.
 */
export function useShellDeepLinks(): void {
  const navigate = useNavigate();
  const { promotionStore } = useStores();

  useEffect(
    () =>
      onShellDeepLink((link) => {
        const url = toDeepLinkUrl(link);
        if (!url) return;

        const { path, promoCode } = mapShellDeepLink(url);
        void navigate(path);
        if (promoCode) {
          void promotionStore.openFromLink(promoCode);
        }
      }),
    [navigate, promotionStore],
  );
}

/**
 * Tells the shell this page has painted (`ui.ready`) on any route but `/`, once the session is known.
 * `/` is left to `Lobby` and the native lander, which hold their own loaders past that point. Without this, a
 * cold start from a deep link to anything else (`/bingo`, `/games/:id`, `NotFound`) unmounts `Lobby` before
 * it can post, and the shell keeps its branded loader over a working page until its 10 s fallback.
 */
export function useShellUiReady(): void {
  const { authStore, userStore } = useStores();
  const { pathname } = useLocation();

  const isOwnedByLobby = pathname === '/';
  const isSessionKnown = authStore.isAuthenticationFlowDone && !userStore.loading;

  useEffect(() => {
    if (!isOwnedByLobby && isSessionKnown) {
      postUiReady();
    }
  }, [isOwnedByLobby, isSessionKnown]);
}
